<script lang="ts">
// See https://class-component.vuejs.org/ for more details on class style components in Vue
import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'
import axios from 'axios';

import 'vue-good-table/dist/vue-good-table.css'
import { VueGoodTable } from 'vue-good-table';

import { ClassDto } from "../models/PublicApi";

@Component
export default class ClassSearch extends Vue {
    // Props
    @Prop({ required: true }) isLoading!: boolean;
    @Prop({ required: true }) searchResults!: Array<any>;
    @Prop({ required: true }) parentSearchParams!: Object;

    // Data
    currentOffset = 15; // Offset by 15 because we already have 15 results loaded
    isLoadingMore = false;
    hasMoreData = true;
    observer: IntersectionObserver | null = null;

    // Methods
    /**
     * Call this method from the parent after a new search to reset infinite scroll state.
     * Example: this.$refs.classSearch.resetInfiniteScrollState();
     */
    resetInfiniteScrollState() {
        this.currentOffset = 15;
        this.hasMoreData = true;
        this.isLoadingMore = false;
        this.observeScrollTrigger();
    }

    async fetchMoreData() {
        // If a new search just happened and searchResults is empty, reset offset
        if (this.searchResults.length === 0) {
            this.currentOffset = 0;
            this.hasMoreData = true;
        }
        if (this.isLoadingMore || !this.hasMoreData) return;

        this.isLoadingMore = true;
        try {
            const response = await axios.get('/api/classes/search', {
                params: {
                    ...this.parentSearchParams, // Use the parent's search params
                    offset: this.currentOffset,
                    limit: 15
                }
            });
            const data = response.data;

            if (data.length > 0) {
                this.$emit('update:searchResults', [...this.searchResults, ...data]);
                this.currentOffset += data.length;
            } else {
                this.hasMoreData = false;
            }
        } catch (error) {
            console.error('Error fetching more data:', error);
        } finally {
            this.isLoadingMore = false;
        }
    }

    onRowClick(row: ClassDto) {
        window.open('/class/' + row.id, '_blank');
    }

    mounted() {
        this.observeScrollTrigger();
    }

    observeScrollTrigger() {
        if (this.observer) {
            this.observer.disconnect();
        }
        this.observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && this.searchResults.length > 0) {
                this.fetchMoreData();
            }
        });
        const scrollTrigger = this.$refs.scrollTrigger as Element | undefined;
        if (scrollTrigger) {
            this.observer.observe(scrollTrigger);
        }
    }

    getDeliveryMethodClass(deliveryMethod: string) {
        switch (deliveryMethod) {
            case 'Remote':
                return 'delivery-remote';
            case 'Self-Paced':
                return 'delivery-self-paced';
            default:
                return 'delivery-in-person';
        }
    }

    @Watch('searchResults')
    onSearchResultsChanged() {
        this.$nextTick(() => {
            this.observeScrollTrigger();
        });
    }    get filteredResults() {
        // Filtering is now handled on the backend
        return this.searchResults;
    }
}
</script>
<template>
    <div>
        <!-- No Results Message -->
        <div v-if="isLoading" class="text-center text-gray-500">
            Searching for Classes...
        </div>
        <div v-else-if="searchResults.length === 0" class="text-center text-gray-500">
            No classes were found that matched your criteria. Please try expanding your search.
        </div>

        <!-- Card Layout -->
        <div v-else class="grid grid-cols-1 gap-6 card-container md:grid-cols-2 lg:grid-cols-3">
            <div v-for="(row, index) in filteredResults" :key="index" class="card">
                <div class="card-content">
                    <div class="top-card">
                        <h3 class="card-title">{{ row.certification }}</h3>
                        <template v-if="row.certificationAbbreviation.includes(',')">
                            <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                                <span v-for="(cert, index) in row.certificationAbbreviation.split(',')" :key="index">
                                    <img 
                                        class="h-auto w-14 badge-img" 
                                        :alt="cert.trim()" 
                                        :src="['IMC-CC', 'IMC-EI', 'IMC-LS', 'IMC-CM'].includes(cert.trim()) 
                                            ? `/media/badges/micro-credentials/${cert.trim()}.png` 
                                            : `/media/badges/${cert.trim()}.svg`">
                                </span>
                            </div>
                        </template>
                        <template v-else>
                            <img 
                                class="h-auto w-14 badge-img" 
                                :alt="row.certification" 
                                :src="row.certificationAbbreviation.startsWith('IMC-') 
                                    ? `/media/badges/micro-credentials/${row.certificationAbbreviation}.png` 
                                    : `/media/badges/${row.certificationAbbreviation}.svg`">
                        </template>
                    </div>
                    <div class="delivery-method" :class="getDeliveryMethodClass(row.location)">
                        {{
                            getDeliveryMethodClass(row.location) === 'delivery-in-person'
                                ? 'In-Person'
                                : getDeliveryMethodClass(row.location) === 'delivery-self-paced'
                                    ? 'On Demand'
                                    : row.location
                        }}
                    </div>

                    <ul class="card-details">
                        <li>
                            <img src="/media/find-a-class/find-a-class-detail-page-instructors.svg" alt="provider icon"
                                class="icon" />
                            {{ row.provider }}
                        </li>
                        <li v-if="row.location !== 'Self-Paced'">
                            <img src="/media/find-a-class/start-date.svg" alt="date icon" class="icon" />
                            {{ row.time.formattedDate }}
                        </li>
                        <li v-if="row.location !== 'Self-Paced'">
                            <img src="/media/find-a-class/class-detail-time.svg" alt="time icon" class="icon" />
                            {{ row.time.timeZone }}
                        </li>
                        <li v-if="row.location == 'Self-Paced'">
                            <img src="/media/find-a-class/class-detail-time.svg" alt="time icon" class="icon" />
                            Self-Paced
                        </li>
                        <li v-if="row.location !== 'Remote' && row.location !== 'Self-Paced'">
                            <img src="/media/find-a-class/find-a-class-detail-page-location.svg" alt="location icon"
                                class="icon" />
                            {{ row.location }}
                        </li>
                        <li>
                            <img src="/media/find-a-class/language.svg" alt="language icon" class="icon" />
                            {{ row.language }}
                        </li>
                        <li v-if="row.guaranteedToRun">
                            <img src="/media/find-a-class/guaranteed-to-run-results.svg" alt="guaranteed to run icon"
                                class="icon" />
                            Guaranteed To Run
                        </li>
                    </ul>
                </div>

                <div class="card-actions">
                    <a class="learn-more" @click="onRowClick(row)">Learn More</a>
                    <a :href="row.registrationUrl" target="_blank" class="register-button register-class">Register</a>
                </div>

            </div>
        </div>

        <!-- Infinite Scroll Trigger -->
        <div ref="scrollTrigger" class="scroll-trigger" v-if="hasMoreData && !isLoadingMore"></div>

        <!-- Loading Spinner -->
        <div v-if="isLoadingMore" class="spinner">
            <div class="spinner-circle"></div>
        </div>

        <!-- End of Search Message -->
        <div v-if="!hasMoreData && searchResults.length > 0 && !isLoadingMore" class="end-of-search">
            End of Search
        </div>
    </div>
</template>

<style>
.card-container {
    display: grid;
    gap: 20px;
    padding: 20px;
}

.card {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    border: 1px solid #595A59;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 10px;
    padding: 16px;
}

.card-content {
    flex-grow: 1;
}

.card-title {
    font-size: 1.25rem;
    font-weight: bold;
    margin-bottom: 4px;
    line-height: normal !important;
    padding-right: 5px;
}

.card-details {
    list-style-type: none;
    padding: 0;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 18px;
    margin-bottom: 16px;
}

.card-details li {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.card-actions {
    display: flex;
    justify-content: space-between;
    margin-right: 5px;
    align-items: center;
    margin-top: auto;
}

.icon {
    width: 25px;
    height: 25px;
    flex-shrink: 0;
}

.delivery-method {
    border-radius: 15px;
    color: white;
    padding: 4px 12px;
    display: inline-block;
    margin-bottom: 8px;
    display: inline-block;
    width: auto;
    align-self: flex-start;
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
    margin-bottom: 16px;
    padding-top: 6px;
}

.delivery-remote {
    background: #D7EFDF;
    color: #003314;
}

.delivery-self-paced {
    background: #E5D7EF;
    color: #4A0F58;
}

.delivery-in-person {
    background: #D7E2EF;
    color: #00558C;
}

.top-card {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
}

.register-button {
    background-color: #F2A900 !important;
    color: black !important;
    padding: 8px 24px !important;
    cursor: pointer;
    display: inline-block;
    align-items: center;
    border-radius: .25rem;
    border-width: 1px;
    border-color: transparent;
    text-align: center;
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 500;
    text-decoration: none;
}

.register-button:hover {
    border-color: white;
}

.learn-more {
    --tw-text-opacity: 1;
    color: rgba(0, 85, 140, var(--tw-text-opacity));
    text-decoration: underline;
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: 500;
    padding-right: 1.5rem;
    padding-left: 2px;
}

.learn-more:hover {
    cursor: pointer;
}

.scroll-trigger {
    height: 1px;
    margin-top: 20px;
}

.spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 24px 0;
}
.spinner-circle {
    border: 4px solid #e5e7eb;
    border-top: 4px solid #00558C;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
}
@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
.end-of-search {
    text-align: center;
    color: #888;
    font-size: 1.1rem;
    margin: 32px 0 16px 0;
    font-weight: 500;
}
</style>